import { useState, useEffect } from 'react';
import api from '../services/api';
import { Check, X, UserPlus, Users, MessageSquare } from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';
import { useLanguage } from '../context/LanguageContext';

function Notifications() {
  const { t } = useLanguage();
  const { refreshCounts } = useNotifications();
  const [notifications, setNotifications] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loadingActions, setLoadingActions] = useState({});

  useEffect(() => {
    fetchNotifications();
    fetchPendingRequests();
  }, []);

  const fetchNotifications = async () => {
    try {
      const { data } = await api.get('/users/me/notifications');
      if (Array.isArray(data)) {
        setNotifications(data);
      }
    } catch (err) {
      console.error('Failed to fetch notifications');
    }
  };

  const fetchPendingRequests = async () => {
    try {
      const { data } = await api.get('/connections/pending');
      if (Array.isArray(data)) {
        setPendingRequests(data);
      }
    } catch (err) {
      console.error('Failed to fetch pending requests');
    }
  };

  const acceptRequest = async (connectionId) => {
    setLoadingActions(prev => ({ ...prev, [connectionId]: true }));
    try {
      await api.post('/connections/accept', { connection_id: connectionId });
      setPendingRequests(prev => prev.filter(r => r.id !== connectionId));
      fetchNotifications(); // Refresh notifications
    } catch (err) {
      console.error('Accept failure:', err);
      alert(err.response?.data?.error || 'Failed to accept request');
    }
    setLoadingActions(prev => ({ ...prev, [connectionId]: false }));
  };

  const rejectRequest = async (connectionId) => {
    setLoadingActions(prev => ({ ...prev, [connectionId]: true }));
    try {
      await api.post('/connections/reject', { connection_id: connectionId });
      setPendingRequests(prev => prev.filter(r => r.id !== connectionId));
    } catch (err) {
      alert('Failed to reject request');
    }
    setLoadingActions(prev => ({ ...prev, [connectionId]: false }));
  };

  const markAsRead = async (notifId) => {
    try {
      await api.put(`/users/me/notifications/${notifId}/read`);
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, is_read: 1 } : n));
      refreshCounts();
    } catch (err) {
      console.error('Failed to mark as read');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'connection_request':
        return <UserPlus size={18} color="var(--accent-primary)" />;
      case 'connection_accepted':
        return <Users size={18} color="var(--accent-primary)" />;
      case 'message':
        return <MessageSquare size={18} color="var(--accent-primary)" />;
      default:
        return <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: 'var(--accent-primary)' }}></div>;
    }
  };

  return (
    <div className="animate-fade" style={{ maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ marginBottom: '3rem', textAlign: 'center' }}>
        <h1 style={{ fontSize: '3rem', color: 'var(--text-heading)', marginBottom: '0.5rem', letterSpacing: '-0.03em' }}>AP <span style={{ color: 'var(--accent-primary)' }}>{t('updates_title')}</span></h1>
        <p style={{ color: 'var(--text-body)', fontWeight: 500 }}>{t('updates_subtitle')}</p>
      </div>

      {/* Pending Connection Requests Section */}
      {pendingRequests.length > 0 && (
        <div style={{ marginBottom: '2rem' }}>
          <h2 style={{ fontSize: '1.25rem', color: 'var(--accent-primary)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <UserPlus size={20} /> {t('pending_requests')}
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {pendingRequests.map(req => (
              <div
                key={req.id}
                className="glass-card"
                style={{
                  padding: '1.25rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  background: 'var(--bg-card)',
                  border: '1px solid var(--glass-border)',
                  boxShadow: 'var(--shadow-inset)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: 'var(--bg-deep)',
                    border: '1px solid var(--glass-border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.25rem',
                    fontWeight: 800,
                    color: 'var(--accent-primary)'
                  }}>
                    {req.full_name?.[0] || req.username?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{req.full_name || req.username}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{req.role_title || t('member_fallback')}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => acceptRequest(req.id)}
                    disabled={loadingActions[req.id]}
                    className="btn-cyber"
                    style={{ padding: '0.5rem 1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                  >
                    <Check size={16} /> {t('accept')}
                  </button>
                  <button
                    onClick={() => rejectRequest(req.id)}
                    disabled={loadingActions[req.id]}
                    className="btn-cyber"
                    style={{ padding: '0.5rem 1rem', background: 'rgba(244, 114, 182, 0.1)', border: '1px solid var(--accent-magenta)', color: 'var(--accent-magenta)', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: 'none' }}
                  >
                    <X size={16} /> {t('decline')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Notifications */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        {notifications.length > 0 ? notifications.map(n => (
          <div
            className="glass-card"
            key={n.id}
            onClick={() => markAsRead(n.id)}
            style={{
              padding: '1.5rem',
              display: 'flex',
              gap: '1.5rem',
              alignItems: 'flex-start',
              background: n.is_read ? 'rgba(28, 33, 40, 0.2)' : 'rgba(99, 102, 241, 0.05)',
              borderColor: n.is_read ? 'var(--glass-border)' : 'var(--accent-primary)',
              borderLeftWidth: n.is_read ? '1px' : '4px',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              cursor: 'pointer',
              boxShadow: n.is_read ? 'var(--shadow-inset)' : '0 10px 20px rgba(99, 102, 241, 0.1), var(--shadow-inset)'
            }}
          >
            <div style={{ marginTop: '0.25rem' }}>
              {getNotificationIcon(n.type)}
            </div>

            <div style={{ flex: 1 }}>
              <p style={{
                fontSize: '1.1rem',
                color: n.is_read ? 'var(--text-body)' : 'var(--text-heading)',
                fontWeight: n.is_read ? '400' : '700'
              }}>
                {n.content}
              </p>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '1rem',
                borderTop: '1px solid var(--glass-border)',
                paddingTop: '0.75rem'
              }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                  {new Date(n.created_at).toLocaleString().toUpperCase()}
                </span>
                <span style={{
                  fontSize: '0.7rem',
                  color: n.is_read ? 'var(--text-muted)' : 'var(--accent-primary)',
                  textTransform: 'uppercase',
                  fontWeight: '800',
                  letterSpacing: '0.1em'
                }}>
                  {n.is_read ? t('archived') : t('new_update')}
                </span>
              </div>
            </div>
          </div>
        )) : (
          <div className="glass-card" style={{ padding: '4rem', textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', opacity: 0.2, marginBottom: '1rem' }}>📡</div>
            <p style={{ color: 'var(--text-muted)', fontFamily: 'Outfit', letterSpacing: '0.2em' }}>
              {t('no_notifications')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Notifications;
